import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getStrapiMediaUrl, useHomeBentoItems } from '../../../lib/strapi';

const isExternalLink = (link: string) => /^https?:\/\//i.test(link);

const promotionItemClassName =
  'group relative block aspect-square w-[calc((100vw-3rem)/2)] max-w-none shrink-0 snap-start overflow-hidden rounded-xl bg-black/5 md:w-[22rem]';

const HomeBentoSkeleton = () => (
  <section aria-label="Cargando promociones" aria-busy="true">
    <div className="content-container">
      <h2 className="mb-6 text-3xl font-bold tracking-tight text-[#0d0d0d] lg:text-4xl">
        Promociones
      </h2>
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className={`${promotionItemClassName} bg-black/10`}
          >
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-black/5 via-white/30 to-black/5" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const HomeBento = () => {
  const { items, loading, error } = useHomeBentoItems();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasMoreToRight, setHasMoreToRight] = useState(false);

  const updateScrollIndicator = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setHasMoreToRight(
      container.scrollLeft + container.clientWidth < container.scrollWidth - 2
    );
  }, []);

  useEffect(() => {
    updateScrollIndicator();
    window.addEventListener('resize', updateScrollIndicator);

    return () => window.removeEventListener('resize', updateScrollIndicator);
  }, [items, updateScrollIndicator]);

  if (loading) {
    return <HomeBentoSkeleton />;
  }

  if (error || items.length === 0) {
    return null;
  }

  return (
    <section aria-label="Promociones destacadas">
      <div className="content-container relative">
        <h2 className="mb-6 text-3xl font-bold tracking-tight text-[#0d0d0d] lg:text-4xl">
          Promociones
        </h2>
        <div
          ref={scrollContainerRef}
          onScroll={updateScrollIndicator}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory"
        >
          {items.map((item, index) => {
            const imageUrl = getStrapiMediaUrl(item.image);
            const alt = item.image?.alternativeText || '';
            const external = isExternalLink(item.link);
            const openInNewTab = item.openInNewTab || external;
            const image = (
              <>
                <img
                  src={imageUrl}
                  alt={alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span className="absolute inset-0 ring-1 ring-inset ring-black/5 transition-colors group-hover:ring-black/15" />
              </>
            );

            return external || openInNewTab ? (
              <a
                key={item.id}
                href={item.link}
                className={promotionItemClassName}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
                aria-label={alt || `Promoción ${index + 1}`}
              >
                {image}
              </a>
            ) : (
              <Link
                key={item.id}
                to={item.link}
                className={promotionItemClassName}
                aria-label={alt || `Promoción ${index + 1}`}
              >
                {image}
              </Link>
            );
          })}
        </div>
        {hasMoreToRight && (
          <div
            className="pointer-events-none absolute inset-y-0 right-4 z-10 w-16 bg-gradient-to-l from-[#f2ede4] via-[#f2ede4]/55 to-transparent md:right-8 md:w-14"
            aria-hidden="true"
          >
            <span className="absolute right-2 top-1/2 h-10 w-0.5 -translate-y-1/2 animate-pulse rounded-full bg-black/40 md:right-3" />
          </div>
        )}
      </div>
    </section>
  );
};

export default HomeBento;
